import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Map, AlertTriangle, CheckCircle, Clock } from 'lucide-react'; // Import necessary icons
import { getMyObservations, getAreas } from '../../services/observationAPI'; // Import API functions
import toast from 'react-hot-toast'; // Import toast for notifications

const MyObservations = () => {
    const [myObservations, setMyObservations] = useState([]);
    const [filteredObservations, setFilteredObservations] = useState([]); // State for filtered observations
    const [areas, setAreas] = useState([]); // State to store areas for filtering
    const [selectedArea, setSelectedArea] = useState(''); // State for selected area filter
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchObservationsAndAreas = async () => {
            try {
                setLoading(true);
                // Fetch observations submitted by the current user and the list of areas
                const [observationsRes, areasRes] = await Promise.all([
                    getMyObservations(),
                    getAreas() // Fetch areas
                ]);

                // Process observations
                const observations = (observationsRes.observations || [])
                    .filter(item => item && typeof item === 'object');
                setMyObservations(observations);
                setFilteredObservations(observations); // Initially show all observations

                // Process areas
                // Assuming areasRes.data is the array of areas
                const areasData = (areasRes.data || []).filter(item => item && typeof item === 'object');
                setAreas(areasData);

            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data.');
                toast.error('Failed to load data.');
                setMyObservations([]);
                setFilteredObservations([]);
                setAreas([]);
            } finally {
                setLoading(false);
            }
        };

        fetchObservationsAndAreas();
    }, []); // Empty dependency array means this effect runs once on mount

    // Effect to filter observations when selectedArea or myObservations changes
    useEffect(() => {
        if (selectedArea) {
            const filtered = myObservations.filter(obs => obs.area?._id === selectedArea);
            setFilteredObservations(filtered);
        } else {
            setFilteredObservations(myObservations); // Show all if no area is selected
        }
    }, [selectedArea, myObservations]); // Rerun when selectedArea or myObservations changes


    // Helper function to format status for display
    const formatStatus = (status) => {
        if (typeof status !== 'string') return 'Unknown Status';
        return status.replace('-', ' ').replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
    };

    // Helper function to get status badge color
    const getStatusBadgeColor = (status) => {
        if (typeof status !== 'string') return 'bg-gray-100 text-gray-800';
        switch (status) {
            case 'waiting':
                return 'bg-amber-100 text-amber-800';
            case 'in-progress':
                return 'bg-purple-100 text-purple-800';
            case 'submitted':
                return 'bg-blue-100 text-blue-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-red-600 mt-8">
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">My Observations</h1>
                <p className="text-gray-600">View the safety observations you have submitted.</p>
            </div>

            {/* Area Filter */}
            <div className="flex items-center space-x-2">
                <label htmlFor="areaFilter" className="block text-sm font-medium text-gray-700">
                    Filter by Area:
                </label>
                <select
                    id="areaFilter"
                    value={selectedArea}
                    onChange={(e) => setSelectedArea(e.target.value)}
                    className="input mt-1 block w-auto" // Adjusted width
                >
                    <option value="">All Areas</option>
                    {areas.map((area) => (
                        <option key={area._id} value={area._id}>
                            {area.name}
                        </option>
                    ))}
                </select>
            </div>


            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                {filteredObservations.length === 0 ? (
                    <div className="px-6 py-4 text-center text-gray-500">
                        {selectedArea ? (
                            <>No observations found for the selected area.</>
                        ) : (
                            <>
                                You haven't submitted any observations yet.
                                <Link to="/employee/submit-observation" className="text-blue-600 hover:underline ml-1">
                                    Submit one now.
                                </Link>
                            </>
                        )}
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {filteredObservations.map((observation) => (
                            <li key={observation._id} className="px-6 py-4">
                                <div className="flex items-start space-x-4">
                                    {/* Icon based on observation type */}
                                    <div className={`flex-shrink-0 p-2 rounded-full ${
                                        observation.type === 'unsafe act' ? 'bg-amber-100' :
                                        observation.type === 'unsafe condition' ? 'bg-red-100' :
                                        'bg-blue-100' // Default color
                                    }`}>
                                         {observation.type === 'unsafe act' || observation.type === 'unsafe condition' ?
                                            <AlertTriangle className={`h-5 w-5 ${observation.type === 'unsafe act' ? 'text-amber-600' : 'text-red-600'}`} /> :
                                            <CheckCircle className="h-5 w-5 text-green-600" /> // Assuming CheckCircle for 'safe' if it were an option
                                         }
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            {/* Display Observation Type and Status */}
                                            <div className="flex items-center space-x-2">
                                                <p className={`text-sm font-medium ${
                                                    observation.type === 'unsafe act' ? 'text-amber-700' :
                                                    observation.type === 'unsafe condition' ? 'text-red-700' :
                                                    'text-gray-900' // Default color
                                                }`}>
                                                    {observation.type === 'unsafe act' ? 'Unsafe Act' :
                                                     observation.type === 'unsafe condition' ? 'Unsafe Condition' :
                                                     observation.type // Display raw type if not one of the known ones
                                                    }
                                                </p>
                                                {/* Display Status Badge */}
                                                {observation.status && (
                                                     <span className={`status-badge text-xs font-semibold px-2.5 py-0.5 rounded-full ${getStatusBadgeColor(observation.status)}`}>
                                                        {formatStatus(observation.status)}
                                                     </span>
                                                )}
                                            </div>
                                            {/* Display Date */}
                                            <p className="text-xs text-gray-500">
                                                {new Date(observation.createdAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                        {/* Display Description */}
                                        <p className="text-sm text-gray-700 mb-2">
                                            {observation.description}
                                        </p>
                                        {/* Display Area and Category */}
                                        <div className="text-xs text-gray-500 space-y-0.5">
                                            <p>Area: {observation.area?.name || 'N/A'}</p> {/* Access area name */}
                                            {observation.category && ( // Only display category if it exists
                                                <p>Category: {observation.category}</p>
                                            )}
                                        </div>
                                        {/* You might want to add a link to view observation details here */}
                                        {/* <Link to={`/employee/observations/${observation._id}`} className="text-sm text-blue-600 hover:underline mt-2 block">View Details</Link> */}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default MyObservations;
